/**
 * The last URL (path and query) this tab showed for each page, per team, so a breadcrumb or Back
 * link to a list returns to it as the person left it: its filters, search and page (resolution 10
 * in the T-150 audit).
 *
 * Kept in memory only: it lasts while the tab runs the app. A team switch reloads the app; joining
 * a team (an invite, Shopify linking) doesn't, but goes to another page, which notes the new team.
 * After a reload a plain link to the list still brings back its filters, through the list's
 * remembered view (`useUrlState`'s `rememberAs`), but not its page.
 */
const MAX_PAGES = 100;
const lastUrls = new Map<string, string>();
/** The team of the page last noted, which is the tab's team. */
let currentTeam: string | null | undefined;

function teamKey(teamId: string | null | undefined, pathname: string): string {
  return `${teamId ?? ''}|${pathname}`;
}

/** Note the URL a page is showing now. PageLayout calls this on every location change. */
export function rememberPageUrl(pathname: string, search: string, teamId: string | null | undefined): void {
  currentTeam = teamId;
  const key = teamKey(teamId, pathname);
  lastUrls.delete(key);
  lastUrls.set(key, search && search !== '?' ? `${pathname}${search}` : pathname);
  if (lastUrls.size > MAX_PAGES) {
    const oldest = lastUrls.keys().next().value;
    if (oldest !== undefined) lastUrls.delete(oldest);
  }
}

/**
 * Where a link back to `path` should go: the URL this tab last showed for it (with its query),
 * or `path` itself when it hasn't been open.
 */
export function listReturnHref(path: string, teamId: string | null | undefined = currentTeam): string {
  return lastUrls.get(teamKey(teamId, path)) ?? path;
}

/** For tests. */
export function forgetPageUrls(): void {
  lastUrls.clear();
  currentTeam = undefined;
}
